import React from 'react';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import { Campaign } from '../../../../support/campaigns';
import OutcomesEditor from './OutcomesEditor';
import QuestionPoolEditor from './QuestionPoolEditor';
import CapabilitiesEditor from './CapabilitiesEditor';
import PersonasEditor from './PersonasEditor';
import TeamRolesEditor from './TeamRolesEditor';

/**
 * Reference Objects — the campaign's catalogue of reusable attributes that STEPS select from (or extend
 * inline). It gathers the libraries that used to sit as separate sections — outcomes, capabilities,
 * customer personas, and workshop team roles — into one tabbed surface, framed as a catalogue rather
 * than as things configured "at the campaign". A step's configuration is where these are picked for a
 * given step (or a new one created), so the campaign level defines the menu and the steps consume it.
 *
 * The libraries are shown one at a time behind top tabs (rather than stacked) so a large catalogue in
 * one library never buries the others.
 *
 * Every library shares the same row model: a row is not edited in place — it shows a read-only summary
 * and an Edit control that expands its config plus a "where it's used" section listing the steps that
 * implement it. The question pool (`CampaignQuestion`) has its own tab here; steps still reference
 * individual questions by category in their Questions panel, and those citations are what the pool's
 * "where it's used" resolves against.
 */
const TABS: { key: string; label: string; render: (p: TabProps) => JSX.Element }[] = [
  { key: 'outcomes', label: 'Outcomes', render: (p) => <OutcomesEditor {...p} /> },
  { key: 'questions', label: 'Question pool', render: (p) => <QuestionPoolEditor {...p} /> },
  { key: 'capabilities', label: 'Capabilities', render: (p) => <CapabilitiesEditor {...p} /> },
  { key: 'personas', label: 'Customer personas', render: (p) => <PersonasEditor {...p} /> },
  { key: 'teamRoles', label: 'Workshop team roles', render: (p) => <TeamRolesEditor {...p} /> },
];

interface TabProps {
  // null = global library mode: manage every global reference object, unscoped to a campaign.
  campaign: Campaign | null;
  canWrite: boolean;
}

export default function ReferenceObjectsEditor(props: {
  campaign: Campaign | null;
  canWrite: boolean;
}): JSX.Element {
  const { campaign, canWrite } = props;
  const [active, setActive] = React.useState(0);
  const current = TABS[active] ?? TABS[0];
  const global = campaign == null;

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary">
        {global
          ? 'The global library of reference objects shared across campaigns. Create, edit, or delete them here; each campaign links the ones it needs from its own Builder. Open a row’s Edit control to configure it and see how many campaigns use it.'
          : 'These are the reusable attributes steps draw on. Define the menu here; each step picks the ones it needs (or creates a new one) in its own configuration. Open a row’s Edit control to configure it and see which steps use it.'}
      </Typography>

      <Tabs
        value={active}
        onChange={(_e, v) => setActive(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)', minHeight: 40 }}
      >
        {TABS.map((t) => (
          <Tab key={t.key} label={t.label} sx={{ minHeight: 40, textTransform: 'none' }} />
        ))}
      </Tabs>

      <Box>{current.render({ campaign, canWrite })}</Box>
    </Stack>
  );
}
